import React, { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import backendApi from "@/api/backendApi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function EmploymentSection({ profile }) {
  const queryClient = useQueryClient();
  const employmentList = profile.employment || [];

  const [isOpen, setIsOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formData, setFormData] = useState({
    designation: "",
    company: "",
    job_type: "Full-time",
    start_date: "",
    end_date: "",
    is_current: false,
    notice_period: "",
  });

  // MUTATIONS
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (editId) {
        return backendApi.entities.UserProfile.updateEmployment(editId, data);
      }
      return backendApi.entities.UserProfile.addEmployment(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
      setIsOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => backendApi.entities.UserProfile.deleteEmployment(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
    },
  });

  const resetForm = () => {
    setEditId(null);
    setFormData({
      designation: "",
      company: "",
      job_type: "Full-time",
      start_date: "",
      end_date: "",
      is_current: false,
      notice_period: "",
    });
  };

  const openAdd = () => {
    resetForm();
    setIsOpen(true);
  };

  const openEdit = (emp) => {
    setEditId(emp.id);
    setFormData(emp);
    setIsOpen(true);
  };

  const handleSave = () => {
    saveMutation.mutate(formData);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this employment record?")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Employment</h3>

        <button
          onClick={openAdd}
          className="text-blue-600 hover:underline text-sm font-medium"
        >
          {employmentList.length > 0 ? "Edit" : "Add"}
        </button>
      </div>

      {/* EMPLOYMENT LIST */}
      <div className="space-y-4">
        {employmentList.length > 0 ? (
          employmentList.map((emp) => (
            <div key={emp.id} className="border-l-2 border-blue-500 pl-4 relative group">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-gray-800 flex items-center gap-2">
                    {emp.designation}
                    <button
                      onClick={() => openEdit(emp)}
                      className="text-gray-400 hover:text-blue-600"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleDelete(emp.id)}
                      className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </h4>

                  <p className="text-gray-600">{emp.company}</p>

                  <p className="text-sm text-gray-500">
                    {emp.job_type} | {emp.start_date} →{" "}
                    {emp.is_current ? "Present" : emp.end_date}
                  </p>

                  {emp.notice_period && (
                    <p className="text-sm text-gray-400">{emp.notice_period}</p>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-gray-500">Add your employment details</p>
        )}
      </div>

      {/* ADD / EDIT DIALOG */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editId !== null ? "Edit Employment" : "Add Employment"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Designation */}
            <div>
              <Label>Designation</Label>
              <Input
                value={formData.designation}
                onChange={(e) =>
                  setFormData({ ...formData, designation: e.target.value })
                }
                placeholder="e.g. Software Engineer"
              />
            </div>

            {/* Company */}
            <div>
              <Label>Company</Label>
              <Input
                value={formData.company}
                onChange={(e) =>
                  setFormData({ ...formData, company: e.target.value })
                }
                placeholder="e.g. Google"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="month"
                  value={formData.start_date}
                  onChange={(e) =>
                    setFormData({ ...formData, start_date: e.target.value })
                  }
                />
              </div>

              <div>
                <Label>End Date</Label>
                <Input
                  type="month"
                  disabled={formData.is_current}
                  value={formData.end_date}
                  onChange={(e) =>
                    setFormData({ ...formData, end_date: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Current Job Checkbox */}
            <div className="flex items-center gap-2">
              <Checkbox
                checked={formData.is_current}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_current: checked })
                }
              />
              <Label>I currently work here</Label>
            </div>

            {/* Notice Period */}
            <div>
              <Label>Notice Period</Label>
              <Input
                value={formData.notice_period}
                onChange={(e) =>
                  setFormData({ ...formData, notice_period: e.target.value })
                }
                placeholder="e.g. 15 Days"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                {editId !== null ? "Update" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
